import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Key, TrendingUp, Users, Shield, Star, ChevronRight } from 'lucide-react';
import PropertyCard from '@/components/PropertyCard';
import PropertySearch from '@/components/PropertySearch';
import SuperhostBadge from '@/components/SuperhostBadge';
import { getProperties, getStats, getTestimonials, seedData } from '@/lib/api';
import { useSiteImage } from '@/contexts/SiteImagesContext';
import { Button } from '@/components/ui/button';

const HOME_HERO_FALLBACK = 'https://customer-assets-39nsmqrw.emergentagent.net/job_bdb51a83-222f-4927-ba7d-46cfdb08edaa/artifacts/61rivrum_47366b2d-0c4d-48fe-a618-d02ce5296d98.jpeg';
const WHY_WORK_FALLBACK = 'https://images.unsplash.com/photo-1668277157922-ecd8330ea809?w=800&q=80';
const STAY_WITH_US_FALLBACK = 'https://images.unsplash.com/photo-1711948728889-614d5d0030f8?w=1920&q=80';

const HomePage = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const heroImage = useSiteImage('hero.home', HOME_HERO_FALLBACK);
  const whyWorkImage = useSiteImage('section.why-work-with-lalita', WHY_WORK_FALLBACK);
  const stayWithUsImage = useSiteImage('section.stay-with-us', STAY_WITH_US_FALLBACK);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first if needed
        await seedData().catch(() => {});
        
        const [propertiesRes, statsRes, testimonialsRes] = await Promise.all([
          getProperties({ featured: true }),
          getStats(),
          getTestimonials(),
        ]);
        
        setFeaturedProperties(propertiesRes.data.slice(0, 6));
        setStats(statsRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('HomePage fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: Building2,
      title: 'Property Management',
      description: 'Full-service villa management including guest services, maintenance, and operations.',
      link: '/property-management',
    },
    {
      icon: Key,
      title: 'Villa Rentals',
      description: 'Premium villa rentals across Bali\'s most sought-after locations.',
      link: '/rentals',
    },
    {
      icon: TrendingUp,
      title: 'Real Estate Brokerage',
      description: 'Expert guidance for buying and selling Bali properties.',
      link: '/real-estate',
    },
    {
      icon: Users,
      title: 'Property Marketing',
      description: 'Professional photography, listings, and OTA optimization.',
      link: '/property-management',
    },
    {
      icon: Shield,
      title: 'Airbnb Setup',
      description: 'Complete setup and optimization for short-term rentals.',
      link: '/property-management',
    },
    {
      icon: Star,
      title: 'Owner Support',
      description: 'Dedicated support and transparent reporting for villa owners.',
      link: '/contact',
    },
  ];

  const advantages = [
    { title: 'Local Bali Team', description: 'Deep local knowledge and connections built over years of experience.' },
    { title: 'Boutique Approach', description: 'Personalized attention to every property and owner we work with.' },
    { title: 'Transparent Communication', description: 'Clear reporting and always accessible when you need us.' },
    { title: 'Guest Experience Focus', description: 'Creating memorable stays that drive positive reviews.' },
    { title: 'Property Optimization', description: 'Maximizing your villa\'s potential through strategic improvements.' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Frangipani flowers floating on rippled water — Lalita Properties Bali"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-semibold leading-tight mb-4 sm:mb-6">
              Bali Villa Management<br />
              <span className="text-white/90 font-normal italic">and Real Estate Services</span>
            </h1>
            <p className="text-white/80 text-base sm:text-lg md:text-xl font-body mb-6 sm:mb-8 max-w-xl">
              Helping villa owners buy, rent, and manage properties with trusted local expertise.
            </p>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <Link
                to="/contact"
                className="bg-ubud-jungle text-white hover:bg-ubud-jungle-dark transition-all duration-300 rounded-full px-6 sm:px-8 py-3 sm:py-3.5 font-body font-medium inline-flex items-center gap-2 text-sm sm:text-base"
                data-testid="hero-list-property-btn"
              >
                List Your Property
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/stays"
                className="bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all duration-300 rounded-full px-6 sm:px-8 py-3 sm:py-3.5 font-body font-medium border border-white/30 text-sm sm:text-base"
                data-testid="hero-experience-btn"
              >
                Experience Lalita
              </Link>
              <SuperhostBadge variant="light" className="ml-0 sm:ml-1" />
            </div>
          </motion.div>

          {/* Search Bar */}
          <div className="mt-8 sm:mt-12 max-w-5xl">
            <PropertySearch variant="hero" />
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-warm-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {[
              { value: stats?.villas_managed ?? '—', label: 'Villas Managed' },
              { value: stats?.total_properties ?? '—', label: 'Listings' },
              { value: `${stats?.years_experience || 10}`, label: 'Years Experience' },
            ].map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="font-heading text-4xl sm:text-5xl font-semibold text-ubud-jungle mb-2">
                  {stat.value}
                </div>
                <div className="font-body text-charcoal-light text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
              Our Services
            </h2>
            <p className="font-body text-charcoal-light text-lg">
              Everything you need to succeed with your Bali property investment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  to={service.link}
                  className="group block bg-warm-sand hover:bg-warm-sand-dark p-8 rounded-3xl transition-all duration-300"
                  data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <service.icon className="w-10 h-10 text-ubud-jungle mb-4" />
                  <h3 className="font-heading text-xl font-medium text-charcoal mb-2 group-hover:text-ubud-jungle transition-colors">
                    {service.title}
                  </h3>
                  <p className="font-body text-charcoal-light text-sm mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-ubud-jungle text-sm font-medium">
                    Learn more <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 lg:py-28 bg-warm-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12"
          >
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
                Featured Properties
              </h2>
              <p className="font-body text-charcoal-light text-lg">
                Handpicked villas from our exclusive collection.
              </p>
            </div>
            <Link
              to="/properties"
              className="inline-flex items-center gap-2 text-ubud-jungle font-medium hover:gap-3 transition-all"
              data-testid="view-all-properties-btn"
            >
              View All Properties <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-96 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property, idx) => (
                <PropertyCard key={property.id} property={property} index={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Work With Lalita */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-6">
                Why Work With <span className="italic text-ubud-jungle">Lalita</span>
              </h2>
              <p className="font-body text-charcoal-light text-lg mb-8">
                We combine local expertise with professional service to help your Bali property thrive.
              </p>
              <div className="space-y-6">
                {advantages.map((adv, idx) => (
                  <motion.div
                    key={adv.title}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-ubud-jungle/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-ubud-jungle" />
                    </div>
                    <div>
                      <h4 className="font-heading text-lg font-medium text-charcoal mb-1">{adv.title}</h4>
                      <p className="font-body text-charcoal-light text-sm">{adv.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={whyWorkImage}
                alt="Bali Villa Interior"
                className="rounded-3xl shadow-floating w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-ubud-jungle text-white p-6 rounded-2xl shadow-lg max-w-xs">
                <div className="font-heading text-2xl font-semibold mb-1">10+ Years</div>
                <div className="font-body text-white/80 text-sm">of local Bali expertise</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Property Owners */}
      <section className="py-20 lg:py-28 bg-charcoal text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={stayWithUsImage}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-6">
                Own a villa in Bali?<br />
                <span className="italic text-white/80">Let us manage it.</span>
              </h2>
              <p className="font-body text-white/70 text-lg mb-8">
                From revenue optimization to guest communication, we handle everything so you can enjoy passive income from your investment.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-charcoal hover:bg-warm-sand transition-all duration-300 rounded-full px-8 py-3.5 font-body font-medium"
                  data-testid="owner-list-property-btn"
                >
                  List Your Property
                </Link>
                <Link
                  to="/property-management"
                  className="border border-white/30 text-white hover:bg-white/10 transition-all duration-300 rounded-full px-8 py-3.5 font-body font-medium"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      {testimonials.length > 0 && (
        <section className="py-20 lg:py-28 bg-warm-sand">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
                Reviews
              </h2>
              <p className="font-body text-charcoal-light text-lg">
                Real reviews from our guests and villa owners across Airbnb, Booking.com & Google.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, idx) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-card flex flex-col"
                  data-testid={`testimonial-${idx}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-temple-gold text-temple-gold" />
                      ))}
                    </div>
                    {testimonial.source && (
                      testimonial.source_url ? (
                        <a
                          href={testimonial.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium px-2.5 py-1 rounded-full bg-ubud-jungle/10 text-ubud-jungle hover:bg-ubud-jungle/20 transition-colors"
                          data-testid={`testimonial-source-${idx}`}
                        >
                          {testimonial.source}
                        </a>
                      ) : (
                        <span
                          className="text-xs font-medium px-2.5 py-1 rounded-full bg-ubud-jungle/10 text-ubud-jungle"
                          data-testid={`testimonial-source-${idx}`}
                        >
                          {testimonial.source}
                        </span>
                      )
                    )}
                  </div>
                  <p className="font-body text-charcoal mb-6 leading-relaxed flex-1">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <div>
                    <div className="font-heading font-medium text-charcoal">{testimonial.name}</div>
                    <div className="font-body text-sm text-charcoal-muted">{testimonial.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA — intentionally removed; the "Own a villa in Bali /
          Let us manage it" dark section above already routes users to
          /contact. Rendering a second full-height white CTA between two
          dark sections (that CTA banner + the footer) created a visible
          white band that read as an "extra empty page" at the bottom of
          the homepage. If you want a second CTA later, wire it as an
          inline banner on a colored background so it doesn't visually
          detach from the surrounding sections. */}
    </div>
  );
};

export default HomePage;
